import {
	Camera,
	ChevronLeft,
	ChevronRight,
	Plus,
	Trash2,
	UserPlus,
} from 'lucide-react'
import { useState } from 'react'
import { Button } from '@/components/occurrences/button'
import {
	ConfidentialityBadge,
	Field,
	JustificationDialog,
	Notice,
	Panel,
	PhotoPlaceholder,
} from '@/components/occurrences/occurrence-ui'
import {
	Dialog,
	DialogContent,
	DialogHeader,
	DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { NativeSelect, NativeSelectOption } from '@/components/ui/native-select'
import { Textarea } from '@/components/ui/textarea'
import { cn } from '@/lib/utils'
import { RESPONSIBLES } from '@/mocks/occurrences/catalog'
import { can, canViewConfidentiality } from '@/mocks/occurrences/rules'
import { newId, useOccurrenceMock } from '@/mocks/occurrences/store'
import type { Confidentiality } from '@/types/occurrence'
import { CONFIDENTIALITY_LABEL } from '@/types/occurrence'
import { useOccurrence } from './occurrence-context'

const TONES = [
	'from-slate-600 to-amber-600',
	'from-zinc-500 to-sky-400',
	'from-stone-600 to-orange-400',
	'from-neutral-600 to-emerald-400',
]
const fmt = (iso: string) =>
	iso
		.replace('T', ' ')
		.split(' ')
		.map((p, i) => (i === 0 ? p.split('-').reverse().join('/') : p))
		.join(' ')

// ─── Evidências e galeria ───────────────────────────────────────────────────
export const EvidenceSection = () => {
	const { occ, profile, editable, userName } = useOccurrence()
	const update = useOccurrenceMock((s) => s.update)
	const [viewIdx, setViewIdx] = useState<number | null>(null)
	const [delId, setDelId] = useState<string | null>(null)
	const [conf, setConf] = useState<Confidentiality>('INTERNAL')

	const all = occ.evidences.filter((e) => !e.deletedReason)
	const visible = all.filter((e) =>
		canViewConfidentiality(profile, e.confidentiality),
	)
	const hidden = all.length - visible.length
	const deleted = occ.evidences.filter((e) => e.deletedReason)
	const cur = viewIdx !== null ? visible[viewIdx] : null

	const add = () => {
		const ev = {
			id: newId(),
			caption: `Foto ${all.length + 1}`,
			author: userName,
			date: new Date().toISOString().slice(0, 10),
			origin: 'Celular',
			confidentiality: conf,
			tone: TONES[all.length % TONES.length],
		}
		update(occ.id, { evidences: [...occ.evidences, ev] }, 'Evidência anexada', {
			detail: `${ev.caption} (${CONFIDENTIALITY_LABEL[conf]}) · varredura antivírus: OK (mock)`,
		})
	}

	return (
		<Panel
			title="Evidências e fotografias"
			description="Metadados de autor, data, origem e confidencialidade. Exclusão é lógica, com justificativa."
			actions={
				editable && (
					<div className="flex items-center gap-2">
						<div className="w-32">
							<NativeSelect
								size="sm"
								value={conf}
								onChange={(e) => setConf(e.target.value as Confidentiality)}
							>
								{(Object.keys(CONFIDENTIALITY_LABEL) as Confidentiality[]).map(
									(c) => (
										<NativeSelectOption key={c} value={c}>
											{CONFIDENTIALITY_LABEL[c]}
										</NativeSelectOption>
									),
								)}
							</NativeSelect>
						</div>
						<Button
							size="sm"
							variant="primary"
							className="gap-1.5"
							onClick={add}
						>
							<Camera className="size-4" /> Anexar foto
						</Button>
					</div>
				)
			}
		>
			{hidden > 0 && (
				<Notice tone="lock">
					{hidden} evidência(s) restrita(s) oculta(s) para o seu perfil.
				</Notice>
			)}
			{visible.length === 0 ? (
				<p className="text-sm text-muted-foreground">
					Nenhuma evidência anexada.
				</p>
			) : (
				<div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
					{visible.map((e, i) => (
						<div
							key={e.id}
							className="group relative overflow-hidden rounded-lg border"
						>
							<button
								type="button"
								className="block w-full"
								onClick={() => setViewIdx(i)}
							>
								<PhotoPlaceholder tone={e.tone} className="h-28 w-full" />
							</button>
							<div className="space-y-1 p-2">
								<p className="line-clamp-1 text-xs font-medium text-kaefer-dark-grey">
									{e.caption}
								</p>
								<ConfidentialityBadge value={e.confidentiality} />
							</div>
							{editable && (
								<button
									type="button"
									aria-label="Excluir evidência"
									onClick={() => setDelId(e.id)}
									className="absolute top-1.5 right-1.5 rounded-full bg-white/90 p-1 opacity-0 shadow group-hover:opacity-100"
								>
									<Trash2 className="size-3.5 text-kaefer-red" />
								</button>
							)}
						</div>
					))}
				</div>
			)}
			{deleted.length > 0 && (
				<p className="text-xs text-muted-foreground">
					{deleted.length} evidência(s) excluída(s) logicamente (preservadas na
					auditoria).
				</p>
			)}

			{/* Carrossel */}
			<Dialog open={cur !== null} onOpenChange={(v) => !v && setViewIdx(null)}>
				<DialogContent className="sm:max-w-2xl">
					<DialogHeader>
						<DialogTitle>{cur?.caption}</DialogTitle>
					</DialogHeader>
					{cur && (
						<div className="space-y-3">
							<div className="relative">
								<PhotoPlaceholder
									tone={cur.tone}
									className="h-72 w-full rounded-lg"
								/>
								<Button
									variant="close-light"
									size="icon"
									className="absolute top-1/2 left-2 -translate-y-1/2"
									onClick={() =>
										setViewIdx(
											(i) => ((i ?? 0) - 1 + visible.length) % visible.length,
										)
									}
								>
									<ChevronLeft className="size-5" />
								</Button>
								<Button
									variant="close-light"
									size="icon"
									className="absolute top-1/2 right-2 -translate-y-1/2"
									onClick={() =>
										setViewIdx((i) => ((i ?? 0) + 1) % visible.length)
									}
								>
									<ChevronRight className="size-5" />
								</Button>
							</div>
							<div className="grid grid-cols-2 gap-2 text-sm sm:grid-cols-4">
								<span>
									<b>Autor:</b> {cur.author}
								</span>
								<span>
									<b>Data:</b> {cur.date.split('-').reverse().join('/')}
								</span>
								<span>
									<b>Origem:</b> {cur.origin}
								</span>
								<ConfidentialityBadge value={cur.confidentiality} />
							</div>
							<p className="text-xs text-muted-foreground">
								{(viewIdx ?? 0) + 1} de {visible.length}
							</p>
						</div>
					)}
				</DialogContent>
			</Dialog>

			<JustificationDialog
				open={delId !== null}
				onOpenChange={(v) => !v && setDelId(null)}
				title="Excluir evidência"
				danger
				confirmLabel="Excluir"
				onConfirm={(why) =>
					update(
						occ.id,
						{
							evidences: occ.evidences.map((e) =>
								e.id === delId ? { ...e, deletedReason: why } : e,
							),
						},
						'Evidência excluída (lógica)',
						{ detail: why },
					)
				}
			/>
		</Panel>
	)
}

// ─── Depoimentos ────────────────────────────────────────────────────────────
export const TestimoniesSection = () => {
	const { occ, editable, userName, profile } = useOccurrence()
	const update = useOccurrenceMock((s) => s.update)
	const audit = useOccurrenceMock((s) => s.audit)
	const [openId, setOpenId] = useState<string | null>(null)
	const [form, setForm] = useState({
		person: '',
		role: '',
		fileName: 'depoimento.pdf',
		ocrText: '',
	})
	const [adding, setAdding] = useState(false)
	const open = occ.testimonies.find((t) => t.id === openId)
	const canValidate = can(profile, 'viewRestricted')

	return (
		<Panel
			title="Depoimentos"
			description="O documento digitalizado é preservado. A transcrição (OCR/IA) precisa ser revisada e validada por pessoa autorizada."
			actions={
				editable && (
					<Button
						size="sm"
						variant="primary"
						className="gap-1.5"
						onClick={() => setAdding(true)}
					>
						<Plus className="size-4" /> Novo depoimento
					</Button>
				)
			}
		>
			<Notice tone="lock">
				Conteúdo restrito: visualizações são registradas na auditoria.
			</Notice>
			{occ.testimonies.length === 0 && (
				<p className="text-sm text-muted-foreground">Nenhum depoimento.</p>
			)}
			<ul className="divide-y">
				{occ.testimonies.map((t) => (
					<li
						key={t.id}
						className="flex flex-wrap items-center justify-between gap-2 py-3"
					>
						<div>
							<p className="text-sm font-medium text-kaefer-dark-grey">
								{t.person}{' '}
								<span className="font-normal text-muted-foreground">
									· {t.role}
								</span>
							</p>
							<p className="text-xs text-muted-foreground">
								{t.fileName} · {t.date.split('-').reverse().join('/')}
							</p>
						</div>
						<div className="flex items-center gap-2">
							<span
								className={cn(
									'rounded-full px-2 py-0.5 text-[11px] font-medium',
									t.ocrValidatedBy
										? 'bg-kaefer-grass-green/20 text-kaefer-forest-green'
										: 'bg-kaefer-honey-yellow/25 text-kaefer-dark-grey',
								)}
							>
								{t.ocrValidatedBy
									? `Transcrição validada por ${t.ocrValidatedBy}`
									: 'Transcrição pendente de validação'}
							</span>
							<Button
								size="sm"
								variant="outline"
								onClick={() => {
									setOpenId(t.id)
									audit(occ.id, `Depoimento visualizado: ${t.person}`, {
										sensitive: true,
									})
								}}
							>
								Abrir
							</Button>
						</div>
					</li>
				))}
			</ul>

			<Dialog open={Boolean(open)} onOpenChange={(v) => !v && setOpenId(null)}>
				<DialogContent className="max-h-[85vh] overflow-y-auto sm:max-w-3xl">
					<DialogHeader>
						<DialogTitle>
							{open?.person} — {open?.fileName}
						</DialogTitle>
					</DialogHeader>
					{open && (
						<div className="grid grid-cols-1 gap-4 md:grid-cols-2">
							<div>
								<p className="mb-1 text-xs font-medium uppercase text-muted-foreground">
									Documento original (preservado)
								</p>
								<div className="flex h-64 items-center justify-center rounded-lg border bg-muted/30 text-sm text-muted-foreground">
									Prévia do PDF digitalizado
								</div>
							</div>
							<div className="space-y-2">
								<p className="text-xs font-medium uppercase text-muted-foreground">
									Transcrição (OCR)
								</p>
								<Textarea
									rows={9}
									value={open.ocrText}
									disabled={!editable || Boolean(open.ocrValidatedBy)}
									onChange={(e) =>
										update(
											occ.id,
											{
												testimonies: occ.testimonies.map((t) =>
													t.id === open.id
														? { ...t, ocrText: e.target.value }
														: t,
												),
											},
											'Transcrição editada',
										)
									}
								/>
								{!open.ocrValidatedBy && canValidate && (
									<Button
										variant="primary"
										onClick={() =>
											update(
												occ.id,
												{
													testimonies: occ.testimonies.map((t) =>
														t.id === open.id
															? { ...t, ocrValidatedBy: userName }
															: t,
													),
												},
												'Transcrição validada',
												{ detail: open.person },
											)
										}
									>
										Validar transcrição
									</Button>
								)}
								{open.ocrValidatedBy && (
									<p className="text-xs text-kaefer-forest-green">
										Validada por {open.ocrValidatedBy}.
									</p>
								)}
							</div>
						</div>
					)}
				</DialogContent>
			</Dialog>

			<Dialog open={adding} onOpenChange={setAdding}>
				<DialogContent className="sm:max-w-md">
					<DialogHeader>
						<DialogTitle>Novo depoimento</DialogTitle>
					</DialogHeader>
					<div className="space-y-3">
						<Field label="Pessoa" required>
							<Input
								value={form.person}
								onChange={(e) => setForm({ ...form, person: e.target.value })}
							/>
						</Field>
						<Field label="Papel">
							<Input
								value={form.role}
								onChange={(e) => setForm({ ...form, role: e.target.value })}
								placeholder="Acidentado, testemunha, encarregado..."
							/>
						</Field>
						<Field label="Texto transcrito (simula OCR)">
							<Textarea
								rows={4}
								value={form.ocrText}
								onChange={(e) => setForm({ ...form, ocrText: e.target.value })}
							/>
						</Field>
					</div>
					<div className="flex justify-end gap-2">
						<Button variant="cancel" onClick={() => setAdding(false)}>
							Cancelar
						</Button>
						<Button
							variant="primary"
							disabled={!form.person.trim()}
							onClick={() => {
								update(
									occ.id,
									{
										testimonies: [
											...occ.testimonies,
											{
												id: newId(),
												person: form.person,
												role: form.role,
												date: new Date().toISOString().slice(0, 10),
												fileName: form.fileName,
												ocrText: form.ocrText,
												ocrValidatedBy: null,
											},
										],
									},
									'Depoimento anexado',
									{
										sensitive: true,
										detail: `${form.person} · por ${userName}`,
									},
								)
								setForm({
									person: '',
									role: '',
									fileName: 'depoimento.pdf',
									ocrText: '',
								})
								setAdding(false)
							}}
						>
							Anexar
						</Button>
					</div>
				</DialogContent>
			</Dialog>
		</Panel>
	)
}

// ─── Comitê ─────────────────────────────────────────────────────────────────
export const CommitteeSection = () => {
	const { occ, editable } = useOccurrence()
	const update = useOccurrenceMock((s) => s.update)
	const [name, setName] = useState('')
	const [role, setRole] = useState('')
	const [area, setArea] = useState('')

	return (
		<Panel
			title="Comitê de investigação"
			description="Participantes registrados com área e papel. Gestores indicam membros; a validação final cabe ao Gestor SSMA."
		>
			{occ.committee.length === 0 && (
				<p className="text-sm text-muted-foreground">
					Comitê ainda não definido.
				</p>
			)}
			<ul className="divide-y">
				{occ.committee.map((m) => (
					<li
						key={m.id}
						className="flex items-center justify-between py-2.5 text-sm"
					>
						<span>
							<b>{m.name}</b>{' '}
							<span className="text-muted-foreground">
								· {m.role} · {m.area}
							</span>
						</span>
						{editable && (
							<Button
								variant="ghost"
								size="icon"
								aria-label="Remover"
								onClick={() =>
									update(
										occ.id,
										{ committee: occ.committee.filter((x) => x.id !== m.id) },
										'Membro do comitê removido',
										{ detail: m.name },
									)
								}
							>
								<Trash2 className="size-4 text-kaefer-red" />
							</Button>
						)}
					</li>
				))}
			</ul>
			{editable && (
				<div className="grid grid-cols-1 items-end gap-3 sm:grid-cols-4">
					<Field label="Nome">
						<NativeSelect
							value={name}
							onChange={(e) => setName(e.target.value)}
						>
							<NativeSelectOption value="">Selecione</NativeSelectOption>
							{RESPONSIBLES.map((r) => (
								<NativeSelectOption key={r} value={r}>
									{r}
								</NativeSelectOption>
							))}
						</NativeSelect>
					</Field>
					<Field label="Papel">
						<Input
							value={role}
							onChange={(e) => setRole(e.target.value)}
							placeholder="Gestor SSMA, Encarregado..."
						/>
					</Field>
					<Field label="Área">
						<Input value={area} onChange={(e) => setArea(e.target.value)} />
					</Field>
					<Button
						variant="primary"
						className="gap-1.5"
						disabled={!name}
						onClick={() => {
							update(
								occ.id,
								{
									committee: [
										...occ.committee,
										{
											id: newId(),
											name,
											role: role || 'Membro',
											area: area || '—',
										},
									],
								},
								'Membro do comitê adicionado',
								{ detail: name },
							)
							setName('')
							setRole('')
							setArea('')
						}}
					>
						<UserPlus className="size-4" /> Adicionar
					</Button>
				</div>
			)}
		</Panel>
	)
}

// ─── Cronologia ─────────────────────────────────────────────────────────────
export const TimelineSection = () => {
	const { occ, editable, userName, locked } = useOccurrence()
	const update = useOccurrenceMock((s) => s.update)
	const [factAt, setFactAt] = useState('')
	const [text, setText] = useState('')
	const [evRef, setEvRef] = useState('')
	const [why, setWhy] = useState<{ id: string } | null>(null)
	const sorted = [...occ.timeline].sort((a, b) =>
		a.factAt.localeCompare(b.factAt),
	)

	const addEvent = (justification?: string) => {
		update(
			occ.id,
			{
				timeline: [
					...occ.timeline,
					{
						id: newId(),
						factAt,
						recordedAt: new Date().toISOString().slice(0, 16),
						description: text.trim(),
						author: userName,
						origin: 'MANUAL',
						evidenceRef: evRef || undefined,
					},
				],
			},
			'Evento incluído na cronologia',
			{ detail: justification ?? text.trim() },
		)
		setFactAt('')
		setText('')
		setEvRef('')
	}

	return (
		<Panel
			title="Cronologia"
			description="Ordenada automaticamente pelo horário do FATO; o horário do REGISTRO é mantido à parte."
		>
			{locked && (
				<Notice tone="lock">
					Investigação validada: novas alterações exigem justificativa e geram
					nova versão auditável.
				</Notice>
			)}
			<ol className="relative space-y-4 border-l-2 border-kaefer-light-silver pl-5">
				{sorted.map((e) => (
					<li key={e.id} className="relative">
						<span
							className={cn(
								'absolute -left-[27px] top-1.5 size-3 rounded-full border-2 border-white',
								e.origin === 'SYSTEM'
									? 'bg-kaefer-silver'
									: 'bg-kaefer-honey-yellow',
							)}
						/>
						<p className="text-xs font-semibold text-kaefer-dark-grey">
							{fmt(e.factAt)}{' '}
							<span className="font-normal text-muted-foreground">(fato)</span>
						</p>
						<p className="text-sm text-kaefer-dark-grey">{e.description}</p>
						<p className="text-[11px] text-muted-foreground">
							Registrado em {fmt(e.recordedAt)} por {e.author} ·{' '}
							{e.origin === 'SYSTEM' ? 'gerado pelo sistema' : 'manual'}
							{e.evidenceRef && ' · com evidência'}
						</p>
					</li>
				))}
				{sorted.length === 0 && (
					<p className="text-sm text-muted-foreground">Sem eventos.</p>
				)}
			</ol>
			{editable && (
				<div className="grid grid-cols-1 items-end gap-3 border-t pt-4 sm:grid-cols-[200px_1fr_180px_auto]">
					<Field label="Data/hora do fato">
						<Input
							type="datetime-local"
							value={factAt}
							onChange={(e) => setFactAt(e.target.value)}
						/>
					</Field>
					<Field label="Descrição">
						<Input
							value={text}
							onChange={(e) => setText(e.target.value)}
							placeholder="O que aconteceu"
						/>
					</Field>
					<Field label="Evidência">
						<NativeSelect
							value={evRef}
							onChange={(e) => setEvRef(e.target.value)}
						>
							<NativeSelectOption value="">Nenhuma</NativeSelectOption>
							{occ.evidences
								.filter((e) => !e.deletedReason)
								.map((e) => (
									<NativeSelectOption key={e.id} value={e.id}>
										{e.caption}
									</NativeSelectOption>
								))}
						</NativeSelect>
					</Field>
					<Button
						variant="primary"
						className="gap-1.5"
						disabled={!factAt || !text.trim()}
						onClick={() => (locked ? setWhy({ id: 'new' }) : addEvent())}
					>
						<Plus className="size-4" /> Incluir
					</Button>
				</div>
			)}
			<JustificationDialog
				open={Boolean(why)}
				onOpenChange={(v) => !v && setWhy(null)}
				title="Incluir evento após validação"
				confirmLabel="Incluir"
				onConfirm={(j) => addEvent(j)}
			/>
		</Panel>
	)
}
